import { createTheme } from "@mui/material/styles";

/**
 * Brand theme, kept deliberately in step with the product app.
 *
 * The site and the app used to look like two different products: the app themes itself green
 * (`merit_badge_college_v2/src/themes/theme.jsx`) with Poppins headings, while this file set no
 * palette at all and inherited MUI's default blue and Roboto. Since the whole point of the site is to
 * hand a prospect straight into the demo, that discontinuity landed at the worst possible moment:
 * the click where someone decides whether this is a real product.
 *
 * The values below are the app's palette verbatim, so a change there is a visible, findable
 * divergence rather than a slow drift nobody owns.
 */

const bodyFont = [
  "-apple-system",
  "BlinkMacSystemFont",
  '"Segoe UI"',
  "Roboto",
  '"Helvetica Neue"',
  "Arial",
  "sans-serif",
].join(",");

// Poppins is self-hosted via @fontsource rather than pulled from Google Fonts: a third-party font
// request is a render-blocking dependency on someone else's uptime, an extra DNS+TLS handshake in
// front of the largest text on the page, and in some jurisdictions a privacy disclosure of its own.
const displayFont = `"Poppins", ${bodyFont}`;

/**
 * The mid green the brand is built around.
 *
 * It measures 3.18:1 on white, so it is only ever safe behind ornament that assistive technology
 * cannot reach: the hero badge backdrop, which is aria-hidden. Nothing carrying text or conveying
 * state may use it in light mode.
 */
export const BRAND_GREEN_DECORATIVE = "#6B9B7E";

/**
 * Brand ramps, one per mode.
 *
 * These used to be a single shared object, on the reasoning that the identity should not change with
 * an OS preference. The problem is that contrast is a relationship between two colours, not a
 * property of one: the mid green clears AA against a near-black dark-mode surface and fails it
 * against a white light-mode one, so a single ramp cannot satisfy both. The identity is preserved by
 * keeping the same hue family in each mode rather than by sharing the same hex.
 */
const lightBrand = {
  light: "#EFFDF3",
  // 6.6:1 on white, so it works for body-sized text, icons, and outlined-button borders alike.
  main: "#006B3F",
  dark: "#004D2E",
  contrastText: "#FFFFFF",
};

const darkBrand = {
  light: "#EFFDF3",
  main: BRAND_GREEN_DECORATIVE,
  dark: "#006B3F",
  contrastText: "#0B1F16",
};

/**
 * Type scale that shrinks on small screens.
 *
 * MUI's default h2 is 3.75rem, which on a 390px-wide phone consumed most of the first screen and
 * pushed the hero's actual value proposition and its call to action below the fold. Responsive
 * font sizes are declared here, once, rather than as breakpoint objects sprinkled through each
 * component, so a new section inherits sensible mobile behaviour instead of having to remember to
 * ask for it.
 */
const typography = {
  fontFamily: bodyFont,
  h1: {
    fontFamily: displayFont,
    fontWeight: 700,
    letterSpacing: "-0.5px",
    fontSize: "2.25rem",
    "@media (min-width:600px)": { fontSize: "3rem" },
    "@media (min-width:900px)": { fontSize: "3.5rem" },
  },
  h2: {
    fontFamily: displayFont,
    fontWeight: 700,
    letterSpacing: "-0.5px",
    fontSize: "1.875rem",
    "@media (min-width:600px)": { fontSize: "2.5rem" },
    "@media (min-width:900px)": { fontSize: "3rem" },
  },
  h3: {
    fontFamily: displayFont,
    fontWeight: 700,
    letterSpacing: "-0.5px",
    fontSize: "1.625rem",
    "@media (min-width:600px)": { fontSize: "2rem" },
    "@media (min-width:900px)": { fontSize: "2.25rem" },
  },
  h4: {
    fontFamily: displayFont,
    fontWeight: 700,
    letterSpacing: "-0.25px",
    fontSize: "1.375rem",
    "@media (min-width:600px)": { fontSize: "1.75rem" },
    "@media (min-width:900px)": { fontSize: "2.125rem" },
  },
  h5: {
    fontFamily: displayFont,
    fontWeight: 700,
    fontSize: "1.1875rem",
    "@media (min-width:600px)": { fontSize: "1.5rem" },
  },
  h6: {
    fontFamily: displayFont,
    fontWeight: 600,
    fontSize: "1.0625rem",
    "@media (min-width:600px)": { fontSize: "1.25rem" },
  },
  button: { fontFamily: displayFont, fontWeight: 600, textTransform: "none" },
};

/** Shared component shaping, so buttons and cards read the same here as in the app. */
const components = {
  MuiCssBaseline: {
    styleOverrides: {
      // Anchors inside prose are theme-coloured rather than browser blue, which was the last
      // remaining place the default palette showed through.
      html: { WebkitFontSmoothing: "antialiased" },
      // Makes the footer sit at the bottom of short pages without anyone having to know how tall the
      // header and footer are. App.js used to subtract hard-coded pixel heights for both, which was
      // wrong on mobile (a 56px toolbar) and wrong again once the footer grew.
      "#root": {
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
      },
      // Long unbroken strings (a pasted URL in an FAQ answer, an email address) are the usual
      // cause of a page that scrolls sideways on a phone.
      "h1, h2, h3, h4, h5, h6, p, li": { overflowWrap: "break-word" },
    },
  },
  MuiButton: {
    defaultProps: { disableElevation: true },
    styleOverrides: {
      root: { borderRadius: 10, paddingInline: 20 },
    },
  },
  MuiCard: {
    styleOverrides: { root: { borderRadius: 14 } },
  },
  MuiPaper: {
    styleOverrides: { rounded: { borderRadius: 14 } },
  },
  MuiContainer: {
    styleOverrides: {
      // MUI's 16px gutter is tight on a phone once card padding is added inside it.
      root: {
        paddingLeft: 20,
        paddingRight: 20,
        "@media (min-width:600px)": { paddingLeft: 24, paddingRight: 24 },
      },
    },
  },
};

const lightTheme = createTheme({
  typography,
  components,
  palette: {
    mode: "light",
    primary: lightBrand,
    secondary: lightBrand,
    // Each status colour is paired with the neutral that clears 4.5:1 against it rather than with a
    // single off-white for all three. #F2F2F3 on amber measured 1.90:1, which is not a legible
    // warning by any definition.
    success: { main: "#15803D", contrastText: "#FFFFFF" },
    warning: { main: "#E8A530", contrastText: "#1A1A1A" },
    error: { main: "#C91D1D", contrastText: "#FFFFFF" },
    footer: {
      backgroundColor: "#fff",
    },
  },
});

const darkTheme = createTheme({
  typography,
  components,
  palette: {
    mode: "dark",
    // Dark mode leads with the mid green rather than the dark one: #006B3F on a near-black surface
    // fails contrast for both text and large UI, so the darker shade is demoted to hover/borders.
    primary: darkBrand,
    secondary: darkBrand,
    success: { main: "#4caf50", contrastText: "#0B1F16" },
    warning: { main: "#E8A530", contrastText: "#0B1F16" },
    error: { main: "#ef5350", contrastText: "#0B1F16" },
    footer: {
      backgroundColor: "#272727",
    },
  },
});

export { lightTheme, darkTheme };
